import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { DOCS } from "@/constants/testIds";
import { NETWORK_CONFIG } from "@/constants/network";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FADE_IN } from "@/lib/motion";
import { useQuery } from "@tanstack/react-query";
import { getNetworkConfig } from "@/lib/api";
import { TokenLogo } from "@/components/common/TokenLogo";
import { tokenLogoUrl } from "@/lib/logos";

function CodeBlock({ children, title }) {
  const copyCode = () => {
    navigator.clipboard.writeText(children);
    toast.success("Code copied");
  };
  return (
    <div className="relative group">
      {title && <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider">{title}</p>}
      <pre className="code-block text-sm overflow-x-auto">
        <code>{children}</code>
      </pre>
      <button
        onClick={copyCode}
        data-testid="docs-copy-btn"
        className="absolute top-3 right-3 opacity-70 group-hover:opacity-100 text-xs text-zinc-500 hover:text-[#D4AF37] bg-[#0A0A0A] px-2 py-1 rounded border border-[#2A2411] transition-opacity"
      >
        Copy
      </button>
    </div>
  );
}

export const OverviewSection = () => {
  return (
    <motion.div {...FADE_IN} className="space-y-6">
      <Card className="border-[#2A2411] bg-[#0A0A0A]">
        <CardContent className="p-6">
          <h2 className="font-display text-xl font-bold mb-4">Overview</h2>
          <p className="text-zinc-300 leading-relaxed mb-4">
            FMI Smart Chain (FSC) is a high-performance EVM-compatible blockchain; your existing Ethereum smart contract skills transfer seamlessly.
          </p>
          <p className="text-zinc-400 leading-relaxed mb-4">
            FSC combines fast finality with low fees, making it ideal for DeFi applications, NFT marketplaces, and enterprise solutions. With full EVM compatibility, you can deploy your existing Solidity contracts without any modifications.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            {[
              { label: "Block Time", value: "~3s" },
              { label: "Finality", value: "~6s" },
              { label: "EVM", value: "Compatible" },
              { label: "Consensus", value: "PoSA" },
            ].map((item) => (
              <div key={item.label} className="text-center p-3 rounded-lg bg-[#050505] border border-[#2A2411]">
                <p className="text-xs text-zinc-500 mb-1">{item.label}</p>
                <p className="font-display font-bold text-[#D4AF37]">{item.value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export const NetworkSection = () => {
  return (
    <motion.div {...FADE_IN} className="space-y-6">
      <Card className="border-[#2A2411] bg-[#0A0A0A]">
        <CardContent className="p-6">
          <h2 className="font-display text-xl font-bold mb-4">Network Configuration</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2A2411]">
                  <th className="text-left py-3 text-zinc-500 font-medium">Property</th>
                  <th className="text-left py-3 text-zinc-500 font-medium">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2A2411]">
                {[
                  ["Network Name", "FMI Smart Chain"],
                  ["Chain ID", "77177"],
                  ["Chain ID (Hex)", "0x12d79"],
                  ["Currency Symbol", "FMI"],
                  ["Decimals", "18"],
                  ["RPC URL", NETWORK_CONFIG.rpcUrls[0]],
                  ["RPC Rate Limit", "300 calls / minute per network (batch items count individually, max 20 per batch)"],
                  ["Block Explorer", NETWORK_CONFIG.blockExplorerUrls[0]],
                  ["Faucet", NETWORK_CONFIG.faucetUrl],
                ].map(([prop, val]) => (
                  <tr key={prop}>
                    <td className="py-3 text-zinc-400">{prop}</td>
                    <td className="py-3 font-mono text-white text-xs">{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export const MetaMaskSection = () => {
  const { addFSCNetwork } = useWallet();
  return (
    <motion.div {...FADE_IN} className="space-y-6">
      <Card className="border-[#2A2411] bg-[#0A0A0A]">
        <CardContent className="p-6 space-y-4">
          <h2 className="font-display text-xl font-bold mb-4">Add FSC to MetaMask</h2>
          <p className="text-zinc-400">
            Click the button below to add FMI Smart Chain to your MetaMask wallet automatically using <code className="text-[#D4AF37]">wallet_addEthereumChain</code>.
          </p>
          <Button
            data-testid={DOCS.addMetamaskBtn}
            onClick={addFSCNetwork}
            className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold rounded-full px-6 transition-transform active:scale-95"
          >
            Add FSC to MetaMask
          </Button>
          <Separator className="bg-[#2A2411] my-4" />
          <p className="text-zinc-500 text-sm">Or add manually with these settings:</p>
          <CodeBlock title="Manual Configuration">{`Network Name: FMI Smart Chain
RPC URL: ${NETWORK_CONFIG.rpcUrls[0]}
Chain ID: 77177
Currency Symbol: FMI
Block Explorer: ${NETWORK_CONFIG.blockExplorerUrls[0]}`}</CodeBlock>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export const HardhatSection = () => {
  return (
    <motion.div {...FADE_IN} className="space-y-6">
      <Card className="border-[#2A2411] bg-[#0A0A0A]">
        <CardContent className="p-6 space-y-4">
          <h2 className="font-display text-xl font-bold mb-4">Hardhat Configuration</h2>
          <p className="text-zinc-400 mb-4">Add FSC to your Hardhat project:</p>
          <CodeBlock title="hardhat.config.js">{`module.exports = {
  networks: {
    fsc: {
      url: "${NETWORK_CONFIG.rpcUrls[0]}",
      chainId: 77177,
      accounts: [process.env.PRIVATE_KEY],
      gasPrice: 5000000000, // 5 Gwei
    },
  },
  solidity: "0.8.24",
};`}</CodeBlock>
          <p className="text-zinc-500 text-sm mt-4">Deploy with:</p>
          <CodeBlock title="Terminal">{`npx hardhat run scripts/deploy.js --network fsc`}</CodeBlock>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export const EthersSection = () => {
  return (
    <motion.div {...FADE_IN} className="space-y-6">
      <Card className="border-[#2A2411] bg-[#0A0A0A]">
        <CardContent className="p-6 space-y-4">
          <h2 className="font-display text-xl font-bold mb-4">ethers.js Integration</h2>
          <CodeBlock title="Connect to FSC">{`import { ethers } from "ethers";

// Read-only provider
const provider = new ethers.JsonRpcProvider(
  "${NETWORK_CONFIG.rpcUrls[0]}",
  { name: "FMI Smart Chain", chainId: 77177 }
);

// Get latest block
const block = await provider.getBlockNumber();
console.log("Latest block:", block);

// Get balance
const balance = await provider.getBalance("0xYourAddress");
console.log("Balance:", ethers.formatEther(balance), "FMI");

// With wallet (browser)
const browserProvider = new ethers.BrowserProvider(window.ethereum);
const signer = await browserProvider.getSigner();
const address = await signer.getAddress();`}</CodeBlock>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export const FoundrySection = () => {
  return (
    <motion.div {...FADE_IN} className="space-y-6">
      <Card className="border-[#2A2411] bg-[#0A0A0A]">
        <CardContent className="p-6 space-y-4">
          <h2 className="font-display text-xl font-bold mb-4">Foundry</h2>
          <p className="text-zinc-400 mb-4">Deploy contracts using Foundry:</p>
          <CodeBlock title="foundry.toml">{`[profile.default]
src = "src"
out = "out"
libs = ["lib"]

[rpc_endpoints]
fsc = "${NETWORK_CONFIG.rpcUrls[0]}"`}</CodeBlock>
          <CodeBlock title="Deploy Command">{`forge create src/MyContract.sol:MyContract \\
  --rpc-url fsc \\
  --chain-id 77177 \\
  --private-key $PRIVATE_KEY`}</CodeBlock>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export const FaqSection = () => {
  return (
    <motion.div {...FADE_IN} className="space-y-4">
      {[
        {
          q: "Is FSC compatible with Ethereum tools?",
          a: "Yes. FSC is fully EVM-compatible. You can use Hardhat, Foundry, Remix, ethers.js, web3.js, and any Ethereum tooling without modifications.",
        },
        {
          q: "What is the native token?",
          a: "FMI is the native gas token of FMI Smart Chain, used for transaction fees, staking, and governance. It has 18 decimals, like ETH.",
        },
        {
          q: "How do I get free FMI?",
          a: "Visit the Faucet page to request free FMI, USDT or FBTC. Each address can request each token once every 5 minutes.",
        },
        {
          q: "What consensus mechanism does FSC use?",
          a: "FSC uses Proof of Staked Authority (PoSA), combining delegated proof-of-stake with proof-of-authority for fast ~3 second block times.",
        },
        {
          q: "How do I add FSC to my wallet?",
          a: `Use the 'Add FSC to MetaMask' button above, or manually add the network using Chain ID 77177 and RPC URL ${NETWORK_CONFIG.rpcUrls[0]}.`,
        },
      ].map((item, i) => (
        <Card key={item.q} className="border-[#2A2411] bg-[#0A0A0A]">
          <CardContent className="p-5">
            <h3 className="font-display text-sm font-bold text-white mb-2">{item.q}</h3>
            <p className="text-zinc-400 text-sm leading-relaxed">{item.a}</p>
          </CardContent>
        </Card>
      ))}
    </motion.div>
  );
};

const LISTING_LINKS = [
  ["CoinGecko listing request", "https://www.coingecko.com/en/coins/new"],
  ["CoinMarketCap listing request", "https://support.coinmarketcap.com/hc/en-us/articles/360043659351-Listings-Criteria"],
  ["Chainlist (add FSC to chainlist.org)", "https://github.com/ethereum-lists/chains"],
];

const InfoTable = ({ rows, testId }) => (
  <div data-testid={testId} className="divide-y divide-[#2A2411] rounded-lg border border-[#2A2411]">
    {rows.map(([k, v]) => (
      <div key={k} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-4 py-2.5 text-sm">
        <span className="text-zinc-500 min-w-[170px]">{k}</span>
        <span className="text-white font-mono break-all">{v || "—"}</span>
      </div>
    ))}
  </div>
);

const AddUsdtButton = ({ usdt }) => {
  const { addUSDTToken, address } = useWallet();
  const add = async () => {
    if (!address) return toast.error("Connect MetaMask first");
    const ok = await addUSDTToken(usdt.address, usdt.decimals, "USDT", tokenLogoUrl(usdt.address));
    toast[ok ? "success" : "error"](ok ? "USDT added to MetaMask" : "Could not add USDT to your wallet");
  };
  return (
    <Button data-testid="docs-add-usdt-btn" onClick={add} variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full">
      <TokenLogo symbol="USDT" size="sm" /> <span className="ml-2">Add USDT to MetaMask</span>
    </Button>
  );
};

export const ListingSection = () => {
  const { addFSCNetwork } = useWallet();
  const { data: config } = useQuery({ queryKey: ["networkConfig"], queryFn: getNetworkConfig });
  const origin = window.location.origin;
  return (
    <motion.div {...FADE_IN} className="space-y-6">
      <Card className="border-[#2A2411] bg-[#0A0A0A]">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-3">
            <TokenLogo symbol="FMI" size="lg" />
            <div>
              <h2 className="font-display text-xl font-bold">Listing Info</h2>
              <p className="text-zinc-400 text-sm">Everything price sites and wallets ask for when listing FMI.</p>
            </div>
          </div>
          <p data-testid="docs-listing-metamask-note" className="text-sm text-zinc-400 leading-relaxed">
            MetaMask shows USD values only for assets that a price provider (CoinGecko / CoinMarketCap) tracks. Until FMI is listed,
            MetaMask shows FMI and USDT balances without a USD figure; FSC Hub's wallet menu and My Tokens page show live USD values from the FSC pool.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="docs-listing-add-network-btn" onClick={addFSCNetwork} className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold rounded-full">
              Add FSC to MetaMask
            </Button>
            {config?.usdt?.address && <AddUsdtButton usdt={config.usdt} />}
          </div>
          <h3 className="font-display text-sm font-bold text-white pt-2">Native coin</h3>
          <InfoTable testId="docs-listing-coin" rows={[
            ["Name", "FMI"], ["Symbol", "FMI"], ["Decimals", "18"], ["Type", "Native gas coin of FMI Smart Chain"],
            ["Logo (PNG)", `${origin}/fmi-logo.png`],
          ]} />
          <h3 className="font-display text-sm font-bold text-white pt-2">Network</h3>
          <InfoTable testId="docs-listing-network" rows={[
            ["Chain name", NETWORK_CONFIG.chainName], ["Chain ID", `${NETWORK_CONFIG.chainId} (${NETWORK_CONFIG.chainIdHex})`],
            ["RPC URL", NETWORK_CONFIG.rpcUrls[0]], ["Block explorer", NETWORK_CONFIG.blockExplorerUrls[0]],
          ]} />
          <h3 className="font-display text-sm font-bold text-white pt-2">Contracts</h3>
          <InfoTable testId="docs-listing-contracts" rows={[
            ["USDT (FSC) token", config?.usdt?.address], ["USDT decimals", config?.usdt?.decimals],
            ["USDT logo (SVG)", config?.usdt?.address && `${origin}/api/tokens/${config.usdt.address}/logo`],
            ["FBTC token", config?.fbtc?.address], ["FBTC decimals", config?.fbtc?.decimals],
            ["FMI/USDT pool", config?.swap?.address], ["Buy router", config?.buyRouter?.address], ["Sell router", config?.sellRouter?.address],
          ]} />
          <h3 className="font-display text-sm font-bold text-white pt-2">Token list</h3>
          <p className="text-sm text-zinc-400">A standard token list (Uniswap format) with USDT and FBTC names, logos and addresses. Wallets and token sites that accept a token list URL can import it.</p>
          <InfoTable testId="docs-listing-tokenlist" rows={[["Token list URL", `${origin}/api/tokenlist.json`], ["Price feed (JSON)", `${origin}/api/v1/prices`]]} />
          <a data-testid="docs-tokenlist-link" href={`${origin}/api/tokenlist.json`} target="_blank" rel="noreferrer" className="inline-block text-sm text-[#D4AF37] hover:underline">Open token list</a>
          <h3 className="font-display text-sm font-bold text-white pt-2">Apply for listings</h3>
          <ul className="space-y-1.5 text-sm">
            {LISTING_LINKS.map(([label, href]) => (
              <li key={href}><a href={href} target="_blank" rel="noopener noreferrer" className="table-link">{label}</a></li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export const DOC_SECTIONS = {
  listing: ListingSection,
  overview: OverviewSection,
  network: NetworkSection,
  metamask: MetaMaskSection,
  hardhat: HardhatSection,
  ethers: EthersSection,
  foundry: FoundrySection,
  faq: FaqSection,
};
